import { useSession } from "@/auth/SessionProvider";
import { ensureDirectoryOrgContext } from "@/lib/ensureDirectoryOrgContext";
import { apiFetchInit, apiUrl } from "@/lib/apiOrigin";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { GlobalAppSlug } from "@/lib/dashboardAppRegistry";
import type { AtwStoredSystemId } from "@/lib/atwSystemAccess";

export type DirectoryMe = {
  id: string;
  email: string;
  name: string | null;
  jobTitle: string | null;
  profilePhoto: string | null;
  role: string;
  status: "active" | "suspended";
  appEntitlements: GlobalAppSlug[] | null;
  systemAccess: AtwStoredSystemId[] | null;
  effectiveSystemAccess: AtwStoredSystemId[];
  /**
   * Explicit Super Admin grant to open Directory → Billing / Credits ledger.
   * Absent or false means only isSuperAdmin may view Billing.
   */
  billingAccessGranted?: boolean;
};

export type DirectoryOrg = {
  orgId: string | null;
  domain: string;
  companyName: string;
  mailEnabled: boolean;
  payEnabled: boolean;
  mfaRequired?: boolean;
};

export type DirectoryContextValue = {
  me: DirectoryMe;
  org: DirectoryOrg | null;
  canManageUsers: boolean;
  isSuperAdmin: boolean;
  assignableAppSlugs: Exclude<GlobalAppSlug, "home">[];
};

type State =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; value: DirectoryContextValue; reload: () => void };

const Ctx = createContext<State>({ status: "loading" });

async function fetchDirectoryMe(): Promise<{ ok: boolean; data: unknown; error?: string }> {
  const res = await fetch(apiUrl("/api/directory/me"), { cache: "no-store", ...apiFetchInit() });
  const data = await res.json();
  if (!res.ok) {
    return { ok: false, data, error: String((data as { error?: string })?.error || "Could not load your account.") };
  }
  return { ok: true, data };
}

function DirectoryContextInner({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const [state, setState] = useState<State>({ status: "loading" });
  const [orgReady, setOrgReady] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await fetchDirectoryMe();
      if (!result.ok) {
        setState({ status: "error", message: result.error || "Could not load your account." });
        return;
      }
      setState({
        status: "ready",
        value: result.data as DirectoryContextValue,
        reload: () => void load(),
      });
    } catch {
      setState({ status: "error", message: "Could not load your account." });
    }
  }, []);

  useEffect(() => {
    if (session.status === "loading") {
      setOrgReady(false);
      return;
    }
    if (session.status === "unauthenticated") {
      setOrgReady(true);
      setState({ status: "error", message: "Sign in to open Directory." });
      return;
    }

    let cancelled = false;
    void (async () => {
      setOrgReady(false);
      setState({ status: "loading" });
      try {
        const bootstrap = await ensureDirectoryOrgContext(session.data?.user?.orgId);
        if (cancelled) return;
        if (bootstrap.sessionUpdated) {
          session.refresh();
        }
      } catch {
        /* load /api/directory/me may still succeed via dashboard-org cookie */
      } finally {
        if (!cancelled) setOrgReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session.status, session.data?.user?.orgId, session.refresh]);

  useEffect(() => {
    if (session.status !== "authenticated" || !orgReady) return;
    void load();
  }, [session.status, orgReady, load]);

  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

export function DirectoryContextProvider({ children }: { children: React.ReactNode }) {
  return <DirectoryContextInner>{children}</DirectoryContextInner>;
}

export function useDirectoryState(): State {
  return useContext(Ctx);
}
